import { Anchor, Group, Loader, Text } from '@mantine/core';
import { IconFile } from '@tabler/icons-react';
import type { Attachment } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react-hooks';
import { useEffect, useState } from 'react';
import { attachmentDataUrl } from '../../../lib/createSecuredAttachment';
import { normalizeBinaryUrl } from '../../../lib/downloadFhirAttachment';
import classes from './ThreadView.module.css';

/**
 * Renders chat attachments using authenticated `medplum.download()` + blob URLs.
 * Avoids `<img src="Binary/...">` / raw links, which resolve against the SPA origin and load the app shell.
 */
export function MessageAttachmentPreview({
  attachment,
  onContentLoaded,
}: {
  attachment: Attachment;
  /** Fired when preview content is ready (blob resolved or image painted). */
  onContentLoaded?: () => void;
}): JSX.Element {
  const medplum = useMedplum();
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let revoked: string | undefined;
    let cancelled = false;

    const run = async (): Promise<void> => {
      const ref = attachment.data && attachment.contentType
        ? attachmentDataUrl(attachment)
        : attachment.url;
      if (!ref) {
        setFailed(true);
        return;
      }
      try {
        const blob = ref.startsWith('data:')
          ? await (await fetch(ref)).blob()
          : await medplum.download(normalizeBinaryUrl(ref));
        if (cancelled) return;
        const typed = attachment.contentType
          ? new Blob([await blob.arrayBuffer()], { type: attachment.contentType })
          : blob;
        revoked = URL.createObjectURL(typed);
        setBlobUrl(revoked);
        const ct = attachment.contentType ?? '';
        if (!ct.startsWith('image/')) {
          onContentLoaded?.();
        }
      } catch {
        if (!cancelled) setFailed(true);
      }
    };

    void run();
    return () => {
      cancelled = true;
      if (revoked) URL.revokeObjectURL(revoked);
    };
  }, [medplum, attachment.url, attachment.data, attachment.contentType, onContentLoaded]);

  if (failed) {
    return (
      <Text size="xs" c="dimmed">
        {attachment.title ?? 'Attachment'} — could not load
      </Text>
    );
  }

  if (!blobUrl) {
    return (
      <Group gap="xs">
        <Loader size="xs" />
        <Text size="xs" c="dimmed">
          Loading…
        </Text>
      </Group>
    );
  }

  const ct = attachment.contentType ?? '';
  const isImage = ct.startsWith('image/');

  if (isImage) {
    return (
      <img
        src={blobUrl}
        alt={attachment.title ?? 'Image'}
        className={classes.attachmentImage}
        onLoad={() => onContentLoaded?.()}
      />
    );
  }

  const label = attachment.title ?? 'Download file';

  return (
    <Anchor href={blobUrl} download={attachment.title} target="_blank" rel="noopener noreferrer" className={classes.attachmentDownloadLink}>
      <Group gap="xs" wrap="nowrap">
        {ct === 'application/pdf' || label.toLowerCase().endsWith('.pdf') ? (
          <IconFile size={18} color="var(--mantine-color-red-6)" />
        ) : (
          <IconFile size={18} />
        )}
        <Text size="sm" c="unifyGold.7" style={{ wordBreak: 'break-word' }}>
          {label}
        </Text>
        {attachment.size != null && (
          <Text size="xs" c="dimmed">
            ({formatBytes(attachment.size)})
          </Text>
        )}
      </Group>
    </Anchor>
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}
